'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import { type Ref, useEffect, useImperativeHandle, useRef } from 'react';
import { CompactItineraryScrollerCard } from '~/components/TripOverview/itinerary/CompactItineraryScrollerCard';
import scrollerStyles from '~/components/TripOverview/itinerary/ExpandedItineraryScroller.module.css';
import type { TripAccommodationStay } from '~/services/engine-ts';
import { type ItemRouteProgress, measureScrollerCards, routeProgressAtFocus, scrollerFocus, scrollerLeftForFocus } from '../lib/cardRouteProgress';
import { transformTripAccommodationToStay } from './transformTripAccommodationToStay';
export type AccommodationStaysMapScrollerHandle = {
  scrollToStay: (itemId: number) => void;
};
type AccommodationStaysMapScrollerProps = {
  stays: TripAccommodationStay[];
  routeProgress: ItemRouteProgress;
  activeItemId: number | null;
  onStaySelect: (itemId: number) => void;
  onProgressChange?: (progress: number) => void;
  ref?: Ref<AccommodationStaysMapScrollerHandle>;
};
export const AccommodationStaysMapScroller = t0 => {
  const $ = _c(25);
  const {
    stays,
    routeProgress,
    activeItemId,
    onStaySelect,
    onProgressChange,
    ref
  } = t0;
  const scrollRef = useRef(null);
  let t1;
  if ($[0] !== onProgressChange || $[1] !== routeProgress) {
    t1 = itemId => {
      const scroller = scrollRef.current;
      if (!scroller) {
        return;
      }
      const cards = measureScrollerCards(scroller);
      const card = cards.find(c => c.id === String(itemId));
      if (!card) {
        return;
      }
      if (scroller.scrollWidth > scroller.clientWidth) {
        scroller.scrollTo({
          left: scrollerLeftForFocus(scroller, card.start),
          behavior: "smooth"
        });
        return;
      }
      onProgressChange?.(routeProgressAtFocus(cards, card.start, routeProgress).progress);
    };
    $[0] = onProgressChange;
    $[1] = routeProgress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const scrollToStay = t1;
  let t2;
  if ($[3] !== scrollToStay) {
    t2 = () => ({
      scrollToStay
    });
    $[3] = scrollToStay;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useImperativeHandle(ref, t2);
  let t3;
  if ($[5] !== onProgressChange || $[6] !== routeProgress) {
    t3 = () => {
      const scroller_0 = scrollRef.current;
      if (!scroller_0 || !onProgressChange) {
        return;
      }
      const compute = () => {
        onProgressChange(routeProgressAtFocus(measureScrollerCards(scroller_0), scrollerFocus(scroller_0), routeProgress).progress);
      };
      compute();
      scroller_0.addEventListener("scroll", compute, {
        passive: true
      });
      return () => scroller_0.removeEventListener("scroll", compute);
    };
    $[5] = onProgressChange;
    $[6] = routeProgress;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== onProgressChange || $[9] !== routeProgress || $[10] !== stays.length) {
    t4 = [onProgressChange, stays.length, routeProgress];
    $[8] = onProgressChange;
    $[9] = routeProgress;
    $[10] = stays.length;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = clsx(scrollerStyles.scrollerClip, scrollerStyles.scrollerClipAccommodation);
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = clsx(scrollerStyles.scroller, scrollerStyles.scrollerAccommodation);
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== activeItemId || $[15] !== onStaySelect || $[16] !== scrollToStay || $[17] !== stays) {
    let t8;
    if ($[19] !== activeItemId || $[20] !== onStaySelect || $[21] !== scrollToStay) {
      t8 = stay => <CompactItineraryScrollerCard key={stay.itemId} item={transformTripAccommodationToStay(stay)} isActive={stay.itemId === activeItemId} accommodationLabel={{
        hotelName: stay.accommodation.title,
        location: stay.placeName
      }} data-test-id={`accommodation-map-card-${stay.itemId}`} onCardClick={() => {
        scrollToStay(stay.itemId);
        onStaySelect(stay.itemId);
      }} />;
      $[19] = activeItemId;
      $[20] = onStaySelect;
      $[21] = scrollToStay;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    t7 = stays.map(t8);
    $[14] = activeItemId;
    $[15] = onStaySelect;
    $[16] = scrollToStay;
    $[17] = stays;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[23] !== t7) {
    t8 = <div className={t5} data-test-id="accommodation-map-scroller"><div ref={scrollRef} className={t6}>{t7}</div></div>;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};